import React from 'react';
import { makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { BonusCreationViewSchema, BonusCreationViewType } from 'src/domains/casino/models/models';
import { slugToLabel } from 'src/domains/casino/utils/utils';
import { BonusCreationState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreation.state';

export class BonusCreationViewManager {
    public constructor(
        private router: RouterBackoffice,
        private readonly common: Common,
        private readonly bonusCreatorState: BonusCreationState
    ) {
        makeAutoObservable(this);
    }

    public bonusRewardsViewsList = BonusCreationViewSchema.options.map((view) => ({
        id: view._def.value,
        value: view._def.value,
        label: slugToLabel(view._def.value),
        notifications: 0,
    }));

    public get currentViewIndex(): number {
        if (this.router.bonusRewardViewParam === null) {
            return 0;
        }

        return this.bonusRewardsViewsList.map((viewItem) => viewItem.value).indexOf(this.router.bonusRewardViewParam);
    }

    public navigateToView = (viewValue: BonusCreationViewType): void => {
        const currentModeType = this.router.bonusCreationParams;
        if (currentModeType?.type === 'NEW') {
            this.router.redirectToBonusCreation({
                type: 'NEW',
                view: viewValue,
                cloneId: currentModeType.cloneId,
            });
        }
        if (currentModeType?.type === 'EDIT') {
            this.router.redirectToBonusCreation({
                type: 'EDIT',
                view: viewValue,
                bonusCreationId: currentModeType.bonusCreationId,
            });
        }
    };

    public handlePrevView = (): void => {
        const prevViewIndex = this.currentViewIndex - 1;
        const prevView = this.bonusRewardsViewsList[prevViewIndex] ?? null;
        if (prevView !== null) {
            this.navigateToView(prevView.value);
        }
    };

    public handleNextView = (): void => {
        const nextViewIndex = this.currentViewIndex + 1;
        const nextView = this.bonusRewardsViewsList[nextViewIndex] ?? null;
        if (nextView !== null) {
            this.navigateToView(nextView.value);
        }
    };

    public handleDiscard = (): void => {
        const popupsStack = PopupsStackState.get(this.common);
        popupsStack.addPopup('close_bonus_creation', {
            template: 'generic',
            title: 'Close Bonus',
            message: (
                <p className='m-0'>
                    Are you sure you want to close this bonus? Progress that was not saved will be lost.
                </p>
            ),
            hasOverlay: true,
            cancel_label: 'Cancel',
            confirm_label: 'Continue',
            onConfirm: () => {
                this.handleClose();
                popupsStack.closePopupOnTop();
            },
            onCancel: () => {
                popupsStack.closePopupOnTop();
            },
        });
    };

    public handleClose = (): void => {
        this.bonusCreatorState.resetIsFormVisited();
        this.router.redirectToBonusCreator();
    };

    public handleCloseWithDiscard = (): void => {
        this.bonusCreatorState.setIsFormTouched();

        if (this.bonusCreatorState.isFormTouched()) {
            this.handleDiscard();
        } else {
            this.handleClose();
        }
    };
}
